import { useMemo } from 'react';
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { Select } from '@shared/ui';
import MetroIcon from '@shared/icons/MetroIcon';
import { apiClient } from '@shared/utils/api-client';
import {
  getMetroStationsForCity,
  hasMetroCatalog,
  resolveMetroIconColor,
} from '../../data/metroStations';
import './PickupPointMetroFields.css';

type MetroFormFields = {
  metro: string;
  metroIconColor: string;
};

export type NearestMetroResponse = {
  name: string;
  lat: number;
  lon: number;
  distanceKm: number;
  city?: string;
};

export async function fetchNearestMetro(
  lat: number,
  lon: number,
): Promise<NearestMetroResponse | null> {
  try {
    return await apiClient.get<NearestMetroResponse>(
      `/geocoding/metro?lat=${encodeURIComponent(String(lat))}&lon=${encodeURIComponent(String(lon))}`,
    );
  } catch {
    return null;
  }
}

export function clearMetroFields<TFieldValues extends FieldValues & MetroFormFields>(
  setValue: UseFormSetValue<TFieldValues>,
) {
  setValue('metro' as Path<TFieldValues>, '' as PathValue<TFieldValues, Path<TFieldValues>>, {
    shouldDirty: true,
  });
  setValue(
    'metroIconColor' as Path<TFieldValues>,
    '' as PathValue<TFieldValues, Path<TFieldValues>>,
    { shouldDirty: true },
  );
}

export type PickupPointMetroFieldsProps<TFieldValues extends FieldValues & MetroFormFields> = {
  city: string;
  /** Показывать только после выбора адреса из списка / когда город известен */
  visible: boolean;
  setValue: UseFormSetValue<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
};

export default function PickupPointMetroFields<
  TFieldValues extends FieldValues & MetroFormFields,
>({ city, visible, setValue, watch }: PickupPointMetroFieldsProps<TFieldValues>) {
  const withCatalog = visible && hasMetroCatalog(city);
  const stations = useMemo(
    () => (withCatalog ? getMetroStationsForCity(city) : []),
    [city, withCatalog],
  );
  const colorByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of stations) map.set(s.name, s.color);
    return map;
  }, [stations]);
  const stationOptions = useMemo(
    () => stations.map((s) => ({ value: s.name, label: s.name })),
    [stations],
  );

  const metroValue = (watch('metro' as Path<TFieldValues>) as string) || '';
  const selectedColor = metroValue
    ? colorByName.get(metroValue) ?? resolveMetroIconColor(city, metroValue)
    : null;

  const applyStation = (stationName: string | null) => {
    const name = (stationName ?? '').trim();
    setValue('metro' as Path<TFieldValues>, name as PathValue<TFieldValues, Path<TFieldValues>>, {
      shouldDirty: true,
    });
    setValue(
      'metroIconColor' as Path<TFieldValues>,
      (name
        ? resolveMetroIconColor(city, name)
        : '') as PathValue<TFieldValues, Path<TFieldValues>>,
      { shouldDirty: true },
    );
  };

  if (!withCatalog) return null;

  return (
    <div className="pickup-point-metro-fields">
      <Select
        label="Метро"
        placeholder="Выберите станцию"
        searchable
        clearable
        nothingFoundMessage="Станция не найдена"
        data={stationOptions}
        value={metroValue || null}
        leftSection={
          selectedColor ? <MetroIcon color={selectedColor} size={14} /> : undefined
        }
        leftSectionPointerEvents="none"
        renderOption={({ option }) => (
          <div className="pickup-point-metro-option">
            <MetroIcon
              color={colorByName.get(String(option.value)) ?? '#E65A8C'}
              size={14}
            />
            <span>{option.label}</span>
          </div>
        )}
        onChange={(value) =>
          applyStation(typeof value === 'string' ? value : value != null ? String(value) : null)
        }
      />
    </div>
  );
}

/** Подставить ближайшее метро и цвет линии по координатам (только города со справочником). */
export async function applyNearestMetroToForm<
  TFieldValues extends FieldValues & MetroFormFields,
>(params: {
  lat: number;
  lon: number;
  city: string;
  setValue: UseFormSetValue<TFieldValues>;
  onlyIfEmpty?: boolean;
  currentMetro?: string;
}): Promise<boolean> {
  const { lat, lon, city, setValue, onlyIfEmpty = false, currentMetro = '' } =
    params;

  if (!hasMetroCatalog(city)) {
    clearMetroFields(setValue);
    return false;
  }

  if (onlyIfEmpty && currentMetro.trim()) return false;

  const nearest = await fetchNearestMetro(lat, lon);
  if (!nearest?.name) return false;

  const catalogMatch = getMetroStationsForCity(city).find(
    (s) =>
      s.name.toLowerCase().replace(/ё/g, 'е') ===
      nearest.name.toLowerCase().replace(/ё/g, 'е'),
  );
  // Только станции из справочника — иначе селект не покажет значение
  if (!catalogMatch) return false;

  setValue(
    'metro' as Path<TFieldValues>,
    catalogMatch.name as PathValue<TFieldValues, Path<TFieldValues>>,
    { shouldDirty: true },
  );
  setValue(
    'metroIconColor' as Path<TFieldValues>,
    catalogMatch.color as PathValue<TFieldValues, Path<TFieldValues>>,
    { shouldDirty: true },
  );
  return true;
}
